// Central analytics module. Wires together:
//  - GA4 (window.gtag)         — loaded only after user consent
//  - Meta Pixel (window.fbq)   — loaded only after user consent
//  - First-party events        — always sent to POST /api/events (no consent needed,
//                                 legitimate interest — anonymous session_id only)
//
// Public API:
//   initAnalytics()   — call once at app boot. Sets up consent state, session_id,
//                        and reads env vars. Loads GA/Meta ONLY if consent was
//                        already granted in a previous visit.
//   grantConsent()    — user clicked "Accept" in the banner → load GA/Meta.
//   denyConsent()     — user clicked "Reject" → GA/Meta never loaded.
//   getConsent()      — "granted" | "denied" | "unknown"
//   track(event, params)      — fires the event to GA (if consented), Meta (if consented),
//                                and first-party (always).
//   trackPageView(path, title) — page_view helper.
//
// Env vars (all optional — the module is a no-op for GA/Meta if unset):
//   REACT_APP_GA4_ID          — e.g. "G-XXXXXXXXXX"
//   REACT_APP_META_PIXEL_ID   — e.g. "123456789012345"

import { API } from "@/lib/api";

const CONSENT_KEY = "pa_consent";           // "granted" | "denied"
const SESSION_KEY = "pa_session_id";        // uuid, resets after 30 min of inactivity
const ANON_KEY = "pa_anon_id";              // uuid, permanent
const SESSION_LAST_ACTIVE_KEY = "pa_session_last";
const SESSION_TTL_MS = 30 * 60 * 1000;      // 30 minutes

const GA_ID = process.env.REACT_APP_GA4_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";

let _initialized = false;
let _gaLoaded = false;
let _metaLoaded = false;

// ---------- id helpers ----------

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback (older Safari): RFC4122 v4
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return "anon-nostore";
  }
}

function getOrRotateSessionId() {
  try {
    const now = Date.now();
    const last = parseInt(localStorage.getItem(SESSION_LAST_ACTIVE_KEY) || "0", 10);
    let id = localStorage.getItem(SESSION_KEY);
    if (!id || !last || now - last > SESSION_TTL_MS) {
      id = uuid();
      localStorage.setItem(SESSION_KEY, id);
    }
    localStorage.setItem(SESSION_LAST_ACTIVE_KEY, String(now));
    return id;
  } catch {
    return "session-nostore";
  }
}

// ---------- consent ----------

export function getConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) || "unknown";
  } catch {
    return "unknown";
  }
}

export function grantConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, "granted");
  } catch {
    /* ignore */
  }
  loadGa();
  loadMeta();
  // Notify listeners so the consent banner can hide itself.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("pa:consent-changed", { detail: "granted" }));
  }
}

export function denyConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, "denied");
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("pa:consent-changed", { detail: "denied" }));
  }
}

// ---------- loaders (only when consent === granted) ----------

function loadGa() {
  if (_gaLoaded || !GA_ID || typeof window === "undefined") return;
  _gaLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, {
    anonymize_ip: true,
    send_page_view: false, // we send page_view ourselves via trackPageView
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);
}

function loadMeta() {
  if (_metaLoaded || !META_PIXEL_ID || typeof window === "undefined") return;
  _metaLoaded = true;
  // Meta Pixel base snippet (from developers.facebook.com).
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", META_PIXEL_ID);
}

// ---------- init ----------

export function initAnalytics() {
  if (_initialized || typeof window === "undefined") return;
  _initialized = true;
  getOrCreateAnonId();
  getOrRotateSessionId();
  if (getConsent() === "granted") {
    loadGa();
    loadMeta();
  }
}

// ---------- track ----------

function readUtms() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || undefined,
      utm_medium: p.get("utm_medium") || undefined,
      utm_campaign: p.get("utm_campaign") || undefined,
      utm_content: p.get("utm_content") || undefined,
      utm_term: p.get("utm_term") || undefined,
    };
  } catch {
    return {};
  }
}

// Standard events reuse GA's own names when applicable; custom events pass
// through as-is (GA4 supports arbitrary event names).
const META_MAP = {
  page_view: "PageView",
  generate_lead: "Lead",
  funnel_start: "InitiateCheckout",
  funnel_submit: "SubmitApplication",
  email_capture: "Lead",
};

async function sendFirstParty(event, params) {
  try {
    await fetch(`${API}/events`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event,
        anonymous_id: getOrCreateAnonId(),
        session_id: getOrRotateSessionId(),
        path: typeof window !== "undefined" ? window.location.pathname : "",
        page_title: typeof document !== "undefined" ? document.title : "",
        referrer: typeof document !== "undefined" ? document.referrer : "",
        language: typeof document !== "undefined" ? document.documentElement.lang : "",
        ...readUtms(),
        params: params || {},
      }),
      keepalive: true,
    });
  } catch {
    // never fatal
  }
}

export function track(event, params = {}, options = {}) {
  // Refresh session activity
  getOrRotateSessionId();

  const consent = getConsent();

  // GA4 — only if consent granted
  if (consent === "granted" && typeof window !== "undefined" && window.gtag) {
    try {
      window.gtag("event", event, params);
    } catch {
      /* ignore */
    }
  }

  // Meta Pixel — only if consent granted. options.eventId is mirrored by the
  // server-side Conversions API copy (event_id) so Meta deduplicates.
  if (consent === "granted" && typeof window !== "undefined" && window.fbq) {
    const metaName = META_MAP[event];
    try {
      if (metaName) {
        window.fbq("track", metaName, params, options.eventId ? { eventID: options.eventId } : undefined);
      } else {
        window.fbq("trackCustom", event, params, options.eventId ? { eventID: options.eventId } : undefined);
      }
    } catch {
      /* ignore */
    }
  }

  // First-party — always
  sendFirstParty(event, params);
}

/** First-party Meta cookies (unhashed per Meta spec) for CAPI matching. */
export function getMetaCookies() {
  const out = { fbp: null, fbc: null };
  try {
    for (const item of document.cookie.split("; ")) {
      const idx = item.indexOf("=");
      const k = item.slice(0, idx);
      if (k === "_fbp") out.fbp = decodeURIComponent(item.slice(idx + 1));
      if (k === "_fbc") out.fbc = decodeURIComponent(item.slice(idx + 1));
    }
  } catch { /* noop */ }
  return out;
}

export function trackPageView(path, title) {
  const params = {
    page_location: typeof window !== "undefined" ? window.location.href : "",
    page_path: path || (typeof window !== "undefined" ? window.location.pathname : ""),
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
  };
  track("page_view", params);
}
